import React from 'react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const ListProperty = () => {
  const benefits = [
    {
      title: 'Targeted Broker Exposure',
      description: 'Your property is instantly broadcasted to our elite network of verified local area brokers with active buyers.'
    },
    {
      title: 'Anti-Fraud Integrity Check',
      description: 'We authenticate every listing dynamically, ensuring serious property seekers engage with absolute trust.'
    },
    {
      title: 'Frictionless Upfront Pricing',
      description: 'No hidden subscription cuts, percentage margins, or backend billing adjustments. Pay a simple flat fee.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Upload Details',
      description: 'Submit your exact rental specifications or land title details along with accurate media imagery.'
    },
    {
      step: '02',
      title: 'Micro-Fee Processing',
      description: 'Process your safe fixed listing transaction securely directly via our centralized payments infrastructure.'
    },
    {
      step: '03',
      title: 'Network Optimization',
      description: 'Your property asset goes live instantly, filtered automatically into our premier broker dashboard matrix.'
    }
  ];

  const stats = [
    { number: '10,000k', label: 'Flat Fee (UGX)' },
    { number: '2.5k+', label: 'Active Assets' },
    { number: '4.8/5', label: 'Broker Feedback' },
    { number: '24hr', label: 'Deployment Turnaround' }
  ];

  return (
    <div className="min-h-screen bg-white text-neutral-900 selection:bg-red-50 selection:text-red-900">
      <Header />
      
      <main className="pt-24">
        
        {/* SECTION 1: HERO (Pristine White Background) */}
        <section className="bg-white tracking-tight">
          <div className="max-w-6xl mx-auto px-6 pt-16 pb-24">
            <div className="mb-8 opacity-70">
              <Breadcrumbs />
            </div>
            
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-4">
                Listing Infrastructure
              </span>
              <h1 className="text-4xl sm:text-6xl font-light text-neutral-950 leading-[1.1] mb-8">
                Position your land or rental asset <span className="font-serif italic text-neutral-500 block sm:inline">before top brokers.</span>
              </h1>
              <p className="text-lg sm:text-xl text-neutral-500 font-light leading-relaxed max-w-2xl">
                Bypass fragmented market layers. Broadcast your property directly into our framework with pre-qualified buyers across East Africa.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <button className="inline-flex items-center justify-center px-6 py-3 bg-neutral-950 text-white text-sm font-medium tracking-tight hover:bg-neutral-900 transition-colors duration-200">
                  List Property Now
                </button>
                <a href="#pricing" className="inline-flex items-center justify-center px-6 py-3 border border-neutral-200 text-neutral-600 text-sm font-medium tracking-tight hover:bg-neutral-50 transition-colors duration-200">
                  View Flat Rate
                </a>
              </div>
            </div>
          </div>
        </section>


        {/* SECTION 2: STATS MATRIX (Soft Chalk Grey Background for Section Breaking) */}
        <section className="bg-neutral-50 border-y border-neutral-200/60">
          <div className="max-w-6xl mx-auto px-6 py-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-y-10 gap-x-4">
              {stats.map((stat, index) => (
                <div key={index} className="sm:border-l sm:border-neutral-200 sm:first:border-l-0 sm:pl-6 first:pl-0">
                  <div className="text-4xl font-light text-red-600 mb-2">
                    {stat.number}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-neutral-500 font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* SECTION 3: THE STRATEGY BENEFITS (Pristine White Background) */}
        <section className="bg-white py-24">
          <div className="max-w-6xl mx-auto px-6">
            <div className="mb-16">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-2">
                Why List With Us
              </span>
              <h2 className="text-3xl font-light text-neutral-950 tracking-tight">Ecosystem Architecture</h2>
            </div>

            <div className="grid lg:grid-cols-3 gap-12 lg:gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="space-y-4">
                  <h3 className="text-xl font-medium text-neutral-950 tracking-tight border-b border-neutral-100 pb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-neutral-500 font-light leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* SECTION 4: THE SUBMISSION PROCESS (Warm Muted Grey Background) */}
        <section className="bg-neutral-100/70 py-24 border-t border-neutral-200/50">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid lg:grid-cols-12 gap-12 items-start">
              <div className="lg:col-span-4">
                <span className="text-xs uppercase tracking-[0.2em] text-neutral-400 font-semibold block mb-2">
                  The Workflow
                </span>
                <h2 className="text-3xl font-light text-neutral-950 tracking-tight">
                  Transparent asset deployment.
                </h2>
                <p className="text-sm text-neutral-500 font-light mt-4 leading-relaxed">
                  We don't overcomplicate distribution. Upload, unlock, and pass structural oversight to specialized real estate matches immediately.
                </p>
              </div>
              
              <div className="lg:col-span-8 space-y-8">
                {process.map((item, index) => (
                  <div key={index} className="flex gap-6 items-start bg-white p-6 border border-neutral-200/40 shadow-sm">
                    <span className="text-xs font-mono font-bold text-red-600 pt-1">
                      {item.step}
                    </span>
                    <div>
                      <h3 className="font-medium text-neutral-950 text-base mb-1">{item.title}</h3>
                      <p className="text-sm text-neutral-500 font-light leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>


        {/* SECTION 5: INTENTIONAL PRICING CARD (Pristine White Background) */}
        <section id="pricing" className="bg-white py-24 border-t border-neutral-100">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <div className="mb-12">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-2">
                Unified Pricing
              </span>
              <h2 className="text-3xl font-light text-neutral-950 tracking-tight">One simple, transparent rate.</h2>
            </div>

            <div className="bg-neutral-50 border border-neutral-200/50 p-12 max-w-md mx-auto text-center space-y-6">
              <span className="text-xs uppercase tracking-widest text-neutral-400 font-medium block">
                Standard Posting
              </span>
              <div>
                <div className="text-5xl font-light text-neutral-950 tracking-tight">
                  10,000 <span className="text-lg font-normal text-neutral-500">UGX</span>
                </div>
                <p className="text-xs text-neutral-400 mt-2">Per unique property or land asset upload</p>
              </div>
              
              <div className="border-t border-neutral-200/60 pt-6 text-left max-w-xs mx-auto space-y-3 text-sm text-neutral-600 font-light">
                <div className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-600"></span>
                  <span>Instant Broker Matrix deployment</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-600"></span>
                  <span>Anti-fraud metadata screening</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-600"></span>
                  <span>Active duration until asset close</span>
                </div>
              </div>

              <button className="w-full py-3 bg-red-600 text-white text-sm font-medium tracking-tight hover:bg-red-700 transition-all duration-200">
                Deploy My Listing
              </button>
            </div>
          </div>
        </section>


        {/* SECTION 6: CTA (Deep Charcoal / Black Background) */}
        <section className="bg-neutral-950 text-white py-24">
          <div className="max-w-4xl mx-auto px-6 text-center space-y-8">
            <span className="text-xs uppercase tracking-[0.2em] text-red-500 font-semibold block">
              Asset Activation
            </span>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight text-white">
              Connect with vetted buyers today.
            </h2>
            <p className="text-neutral-400 font-light max-w-xl mx-auto text-sm sm:text-base">
              Submit your property infrastructure parameters for validation and experience seamless real estate connectivity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <button className="inline-flex items-center justify-center px-6 py-3 bg-red-600 text-white text-sm font-medium tracking-tight hover:bg-red-700 transition-colors duration-200">
                Create Listing (10,000 UGX)
              </button>
              <button className="inline-flex items-center justify-center px-6 py-3 border border-neutral-800 text-neutral-300 text-sm font-medium tracking-tight hover:bg-neutral-900 transition-colors duration-200">
                Contact Infrastructure Support
              </button>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default ListProperty;